import ClassCard from "../SelectedClassesListStep/ClassCard";
import { SeedCluster, SeedItem } from "hooks/LabelPropagation/useGetSeed";
import { ProgressCardProps } from "./ProgressCard";
import VLCollapseCard, {
  CollapseCardItem,
} from "views/uikit/Cards/VLCollapseCard";
import { ReactNode, useMemo } from "react";
import VLMessage from "views/uikit/VLMessage";
import ProgressCardList from "./ProgressCardList";
import { useLocation, useNavigate, useParams } from "react-router";
import { StatsState } from "hooks/LabelPropagation/useGetStats";
import {
  DATA_PAGE_PARAMETER,
  FLOW_PARAMETER,
  LABEL_CLASS_PARAMETER,
  REVIEW_PARAMETER,
  REVIEW_TAB_PARAMETER,
  SEED_PARAMETER,
  VERTEX_CONTEXT,
  VERTEX_ID,
  VERTEX_SIMILARITY,
  VERTEX_TYPE,
} from "helpers/constants/miscellaneous";
import { LoadingOutlined } from "@ant-design/icons";
import { formatNumber } from "helpers/utility/formatters";
import VLColorfulLoader from "views/uikit/VLColorfulLoader";
import { useExportSelection } from "hooks/useExportSelection";
import VLProgressBar, {
  VLProgressBarProps,
} from "views/uikit/ProgressBars/VLProgressBar";
import styles from "./style.module.scss";
import { useDispatch } from "react-redux";
import {
  setNavigationCluster,
  setSimilarityVertex,
} from "../../../../redux/SingleDataset/actions";

export interface ProgressCardsProps {
  mainProgress: VLProgressBarProps;
  items: ProgressCardProps[];
  totalCount?: string;
}

interface ProgressStepProps {
  initialFlowId?: string | null;
  seedId?: string | null;
  stats?: StatsState;
  loadingType?: LoadingType;
  seed?: SeedCluster[];
}

export type LoadingType = "RUN" | "SUBMIT" | "FINISH";

const CARD_KEYS = {
  REVIEW: "review",
  TRAIN_GOAL: "train-worthy-goal",
  SEED: "seed",
} as const;

export default function ProgressStep({
  initialFlowId,
  seedId,
  loadingType,
  stats,
  seed,
}: ProgressStepProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { datasetId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const isReviewOpen = searchParams.get(REVIEW_PARAMETER) === "true";

  const { handleClearAll } = useExportSelection();

  const activeCards: string[] = useMemo(() => {
    let cards: string[] = [];
    if (stats?.review) {
      cards.push(CARD_KEYS.REVIEW);
    }

    return cards;
  }, [stats]);

  const items: CollapseCardItem[] = [
    ...(stats?.review && loadingType !== "SUBMIT"
      ? [
          {
            header: {
              label: "New Batch For Review",
              progress: stats.review.mainProgress,
            },
            key: CARD_KEYS.REVIEW,
            textButton: stats.review.totalCount,
            children: !isReviewOpen ? (
              <VLMessage
                onClick={() => {
                  const flow = initialFlowId;
                  if (!datasetId || !seedId || !flow) return;
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.set(SEED_PARAMETER, seedId);
                  searchParams.set(FLOW_PARAMETER, flow);
                  searchParams.set(REVIEW_PARAMETER, "true");
                  searchParams.set(REVIEW_TAB_PARAMETER, "For Review");
                  searchParams.delete(DATA_PAGE_PARAMETER);
                  [
                    VERTEX_SIMILARITY,
                    VERTEX_CONTEXT,
                    VERTEX_ID,
                    VERTEX_TYPE,
                  ].forEach((p) => searchParams.delete(p));
                  dispatch(setSimilarityVertex(null));
                  const classWithItemsForReview = stats.review.items.find(
                    (i) => i.itemLength !== i.maxItems,
                  );
                  if (classWithItemsForReview) {
                    searchParams.set(
                      LABEL_CLASS_PARAMETER,
                      classWithItemsForReview.id,
                    );
                  }
                  dispatch(setNavigationCluster(null));
                  handleClearAll();
                  navigate(
                    {
                      pathname: `/dataset/${datasetId}/data`,
                      search: searchParams.toString(),
                    },
                    { replace: false },
                  );
                }}
                title="New images are waiting for your review"
                description={`A new set of images is ready for your review.\nLabel Propagation is paused until this review is completed.`}
                textButton="Review"
              />
            ) : (
              <ProgressCardList
                items={stats.review.items}
                text={"Images for review"}
              />
            ),
          },
        ]
      : []),
    {
      header: {
        label: "Train-worthy Goal",
        progress: stats?.labels?.mainProgress,
      },
      textButton: stats?.labels?.totalCount,
      key: CARD_KEYS.TRAIN_GOAL,
      children: stats?.labels ? (
        <ProgressCardList items={stats.labels.items} type="TRAIN_GOAL" />
      ) : null,
    },
    {
      header: { label: "Seed" },
      key: CARD_KEYS.SEED,
      textButton: formatNumber(
        seed?.reduce((total, s) => total + s.items.length, 0) || 0,
      ),
      children: (
        <div className={styles.seedWrapper}>
          {seed?.map((seed: SeedCluster, clusterIdx: number) => (
            <div key={seed.label + clusterIdx} className={styles.seedItem}>
              <ClassCard
                items={seed.items.map((sample: SeedItem) => ({
                  id: sample.id,
                  imgUri: sample.imgUri,
                  originUri: sample.originUri,
                }))}
                value={seed.label}
                classId="null"
                min={5}
                progressValue={seed.count}
                pausedForReview
              />
            </div>
          ))}
        </div>
      ),
    },
  ];

  const mainProgress = stats?.mainProgress;

  const renderProgressContent = () => {
    if (!mainProgress) {
      return (
        <div className={styles.progressContentSpinner}>
          <LoadingOutlined spin={false} />
        </div>
      );
    }

    return (
      <div className={styles.mainProgressContent}>
        {loadingType === "SUBMIT" && <LoadingOutlined spin={false} />}
        {Math.floor((mainProgress.itemLength * 100) / mainProgress.maxItems)}%
      </div>
    );
  };

  const renderLoaderByType: Record<LoadingType, ReactNode | undefined> = {
    RUN: <VLColorfulLoader text={"Running Label Propagation..."} />,
    SUBMIT: undefined,
    FINISH: (
      <VLColorfulLoader
        text={"Applying your feedback - label propagation in progress..."}
      />
    ),
  };
  const isReviewSubmitting = loadingType === "SUBMIT";

  if (loadingType && !isReviewSubmitting) {
    return (
      <div className={styles.mainLoader}>{renderLoaderByType[loadingType]}</div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.totalReviewProgress}>
        <div className={styles.title}>
          <span>Total Review Progress</span>
          <span className={styles.count}>
            Reviewed {mainProgress?.itemLength} / {mainProgress?.maxItems}
          </span>
        </div>
        <div className={styles.mainProgress}>
          <VLProgressBar
            type={"primary"}
            isActive={isReviewSubmitting}
            maxItems={mainProgress?.maxItems || 0}
            itemLength={mainProgress?.itemLength || 0}
          />
          {renderProgressContent()}
        </div>
      </div>

      {
        <VLCollapseCard
          items={items}
          defaultOpenKeys={activeCards}
          onClose={(key) => {
            if (key === "review") {
              const searchParams = new URLSearchParams(location.search);
              searchParams.delete(REVIEW_PARAMETER);
              searchParams.delete(LABEL_CLASS_PARAMETER);
              navigate(
                {
                  pathname: location.pathname,
                  search: searchParams.toString(),
                },
                { replace: true },
              );
            }
          }}
        />
      }
    </div>
  );
}
